import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { ApolloError } from '@apollo/client'

import { ModifierGroup } from 'src/apollo/onlineOrdering'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import NotFound404 from 'shared/components/errors/NotFound404'

import { MenuBanner } from 'public/components/default_template/menu_banner/MenuBanner'

import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { EcommStickyFooter } from 'public/components/default_template/EcommStickyFooter'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import PageMeta from 'public/components/default_template/meta/PageMeta'
import { ItemResolutionModal } from 'public/components/default_template/online_ordering/item_modal/ItemResolutionModal'
import {
  EcommercePageLayout,
  EcommercePageLayoutChildProps
} from 'public/components/default_template/online_ordering/pages/EcommercePageLayout'
import { ModifierContextProvider } from 'public/components/online_ordering/ModifierContext'
import { Action } from 'public/components/online_ordering/types'

import {
  EcommercePage,
  ImageRatio,
  MenuConfig,
  MenuTemplate,
  StyleIntensity
} from 'src/apollo/sites'
import { ProductDisplay } from './ProductDisplay'
import { useDisplayPageContext } from '../../DisplayPageContext'
import {
  useDoMenuItemWithFallback,
  useMenuItemDetailsWithFallback
} from '../hooks/menuItemQueries'
import {
  useProductVariants,
  Product,
  ProductVariant
} from '../hooks/useProductVariants'
import { collapseModifierGroups } from 'public/components/online_ordering/modifierUtils'

type LocationState = {
  menuItem?: MenuItem
  product?: Product
}

type ProductDisplayPageContentProps = EcommercePageLayoutChildProps & {
  ecommContent?: EcommercePage | null
}

export const ecommContentToMenuConfig = (
  content: EcommercePage | null | undefined
): MenuConfig | null => {
  const config = content?.config
  if (!config) {
    return null
  }
  return {
    format: {
      template:
        (config.format?.template as MenuTemplate) ?? MenuTemplate.TopImageV2,
      borderStroke: StyleIntensity.Subtle,
      roundedCorner: StyleIntensity.Subtle,
      condenseImagelessGroups: true,
      dropShadow: StyleIntensity.None,
      imageRatio: ImageRatio.Shorter
    },
    colors: config.colors ?? undefined
  }
}

export const ProductDisplayPageContent = ({
  itemGuid,
  itemGroupGuid,
  itemSlug,
  variationSlug,
  ecommContent,
  groupings
}: ProductDisplayPageContentProps) => {
  const { track } = useThrottledTracker()
  const productDisplayPageViewRef = useRef(false)
  const { restaurant: siteRestaurant } = useRestaurant()
  const { channelGuid } = useDisplayPageContext()
  const { state } = useLocation<LocationState>()
  const [showFooter, setShowFooter] = useState(false)
  const ecommMenuConfig = ecommContentToMenuConfig(ecommContent)
  const history = useHistory()
  const { ecommPath } = useRestaurantRoutes()

  const setFiltersFromPdp = useCallback(
    (filters: React.SetStateAction<Set<string>>) => {
      const next = typeof filters === 'function' ? filters(new Set()) : filters
      const query =
        next.size > 0 ? `?filters=${Array.from(next).join(',')}` : ''
      history.push(`${ecommPath ?? ''}${query}`)
    },
    [history, ecommPath]
  )

  const {
    data,
    error,
    loading: menuItemDetailsLoading
  } = useMenuItemDetailsWithFallback(itemGuid, itemGroupGuid, itemSlug)

  // massage menuItemDetails into the MenuItem type (only for regular menu items)
  const menuItem: MenuItem | undefined | null = useMemo(
    () =>
      data?.menuItemDetails
        ? {
            ...data?.menuItemDetails,
            guid: itemGuid || state?.menuItem?.guid || null,
            itemGroupGuid: itemGroupGuid || ''
          }
        : state?.menuItem,
    [data?.menuItemDetails, state?.menuItem, itemGroupGuid, itemGuid]
  )

  const {
    data: doMenuItemData,
    loading: doMenuItemLoading,
    isVomOnly
  } = useDoMenuItemWithFallback(itemGuid, itemGroupGuid, itemSlug)

  // Fetch product with variants if variationSlug is provided
  // Prefer the query result, but fall back to router state
  const {
    product: productFromQuery,
    loading: productLoading,
    error: productError
  } = useProductVariants(variationSlug)

  const product = productFromQuery || state?.product

  const rootRef = useRef<HTMLDivElement>(null)
  const modifierGroups = useMemo(
    () =>
      data?.menuItemDetails.modifierGroups
        ? collapseModifierGroups(data?.menuItemDetails.modifierGroups)
        : {},
    [data]
  )

  // Track selected variation for updating root modifier
  const [selectedVariation, setSelectedVariation] =
    useState<ProductVariant | null>(null)

  const rootModifier = useMemo(
    () => ({
      groupGuid:
        selectedVariation?.itemGroupGuid ??
        itemGroupGuid ??
        data?.menuItemDetails.itemGroupGuid ??
        'root',
      modifier: {
        itemGuid:
          selectedVariation?.guid ?? (itemGuid ?? data?.menuItemDetails.guid)!,
        itemGroupGuid:
          selectedVariation?.itemGroupGuid ??
          itemGroupGuid ??
          data?.menuItemDetails.itemGroupGuid,
        modifierGroups: data?.menuItemDetails.modifierGroups ?? [],
        price:
          selectedVariation?.prices?.[0] ?? data?.menuItemDetails.prices[0] ?? 0
      },
      modifierGroups,
      isRoot: true,
      masterId: data?.menuItemDetails?.masterId,
      action: 'replace' as Action,
      price:
        selectedVariation?.prices?.[0] ?? data?.menuItemDetails.prices[0] ?? 0,
      usesFractionalQuantity: data?.menuItemDetails?.usesFractionalQuantity,
      unitOfMeasure: data?.menuItemDetails?.unitOfMeasure,
      name: selectedVariation?.name ?? data?.menuItemDetails.name
    }),
    [
      itemGuid,
      itemGroupGuid,
      data?.menuItemDetails,
      modifierGroups,
      selectedVariation
    ]
  )

  const onVariationSelect = useCallback((variation: ProductVariant | null) => {
    setSelectedVariation(variation)
  }, [])

  const doMenuItem =
    !doMenuItemLoading &&
    doMenuItemData?.doMenus_findMenuItem?.__typename ===
      'DoMenus_FindMenuItemResponse'
      ? doMenuItemData?.doMenus_findMenuItem
      : undefined
  const isAvailableNow =
    (!!doMenuItem?.menuItem.isAvailableNow && !isVomOnly) || !!variationSlug

  const modifierMap = useMemo(() => {
    if (isAvailableNow) {
      return doMenuItem?.menuResponse?.modifierGroupReferences?.reduce(
        (map, ref) => ({
          ...map,
          [ref.guid]: ref
        }),
        {}
      )
    } else {
      return {}
    }
  }, [isAvailableNow, doMenuItem])

  useEffect(() => {
    if (typeof window !== 'undefined' && !productDisplayPageViewRef.current) {
      let params
      if (menuItem) {
        params = {
          name: menuItem.name,
          guid: menuItem.guid,
          outOfStock: !!menuItem.outOfStock,
          hasImage: Boolean(menuItem.image)
        }
      } else if (product) {
        params = {
          name: product.name,
          guid: product.guid,
          outOfStock: product.outOfStock,
          hasImage: Boolean(product.imageLink)
        }
      } else if (error && itemNotFound(error)) {
        params = { error: MENU_ITEM_NOT_FOUND }
      } else if (productNotFound(productError)) {
        params = { error: PRODUCT_NOT_FOUND }
      }
      if (params) {
        track(TrackingEvents.PRODUCT_DISPLAY_PAGE_VIEW, {
          ...params,
          itemSlug,
          variationSlug
        })
        productDisplayPageViewRef.current = true
      }
    }
  }, [track, menuItem, product, error, productError, itemSlug, variationSlug])

  const [pageMetaTitle, pageMetaDescription] = useMemo(() => {
    const restaurantName = siteRestaurant?.name || ''

    const itemName = menuItem?.name || undefined
    const defaultTitle = 'Product Display Page'
    const formattedTitleRestaurantDetails = restaurantName
      ? ` | ${restaurantName}`
      : ''
    const titleMeta = `${
      itemName || defaultTitle
    }${formattedTitleRestaurantDetails}`

    const itemDescr = menuItem?.description || ''
    const itemDescrEndsWithPunctuation =
      itemDescr.endsWith('.') || itemDescr.endsWith('!')
    const formattedItemDescr = itemDescr ? `: ${itemDescr}` : ''
    const descrPunctuation = itemDescrEndsWithPunctuation ? '' : '!'
    const formattedDescrRestaurantDetails = restaurantName
      ? ` Try it today at ${restaurantName}!`
      : ''
    const descrMeta = itemName
      ? `Enjoy our ${itemName}${formattedItemDescr}${descrPunctuation}${formattedDescrRestaurantDetails}`
      : undefined

    return [titleMeta, descrMeta]
  }, [menuItem, siteRestaurant?.name])

  return (
    <>
      <PageMeta title={pageMetaTitle} description={pageMetaDescription} />
      {ecommMenuConfig && (
        <MenuBanner
          menuGroups={groupings ?? []}
          setFilters={setFiltersFromPdp}
          channelGuid={channelGuid}
          isEcomm
        />
      )}
      <div className='pageContent'>
        <div className='paddedContentWrapper productDisplayWrapper'>
          {(error && itemNotFound(error)) || (productError && !product) ? (
            <NotFound404
              meta={siteRestaurant?.meta}
              siteTheme={siteRestaurant?.theme}
            />
          ) : (
            <ModifierContextProvider
              modifier={rootModifier}
              rootRef={rootRef}
              modifierGroupData={modifierMap || {}}
              quantity={1}
            >
              <ProductDisplay
                loading={menuItemDetailsLoading || productLoading}
                menuItem={menuItem}
                modifierGroups={
                  (data?.menuItemDetails?.modifierGroups as ModifierGroup[]) ||
                  []
                }
                setShowFooter={setShowFooter}
                isAvailableNow={isAvailableNow}
                variationSlug={variationSlug}
                product={product}
                onVariationSelect={onVariationSelect}
              />
              <ItemResolutionModal channelGuid={channelGuid} />
              <EcommStickyFooter showFooter={showFooter} />
            </ModifierContextProvider>
          )}
        </div>
      </div>
    </>
  )
}

const MENU_ITEM_NOT_FOUND = 'MENU_ITEM_NOT_FOUND'
const PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND'

const itemNotFound = (error: ApolloError | undefined) =>
  error?.graphQLErrors?.some(
    (err: any) => err?.extensions?.code === MENU_ITEM_NOT_FOUND
  )

const productNotFound = (error: ApolloError | undefined) =>
  error?.graphQLErrors?.some(
    (err: any) => err?.extensions?.code === PRODUCT_NOT_FOUND
  )

type ProductDisplayPageProps = {
  ecommContent?: EcommercePage | null
}

const ProductDisplayPage = ({ ecommContent }: ProductDisplayPageProps) => {
  return (
    <EcommercePageLayout>
      {(props) => (
        <ProductDisplayPageContent {...props} ecommContent={ecommContent} />
      )}
    </EcommercePageLayout>
  )
}
export default React.memo(ProductDisplayPage)
